import type { ComponentType } from "react";
import type {
	BlockOf,
	CatalogContentBlock,
	CatalogContentBlockType
} from "@/domain/catalog";

import { FeatureHighlight } from "../blocks/FeatureHighlight";
import { UpsellSet } from "../blocks/UpsellSet";
import { SeeInAction } from "../blocks/SeeInAction";
import { RecipePairings } from "../blocks/RecipePairings";
import { UseAndCare } from "../blocks/UseAndCare";

/**
 * No-op for a template-consumed block: `ProductDetailTemplate` reads it and
 * filters it out of `remainingBlocks`, so the dispatcher never renders it —
 * this entry exists only to keep `ContentBlocksMap` exhaustive over
 * `CatalogContentBlockType`. (`use_and_care_block` predates this and keeps
 * its own `UseAndCare` no-op file; `about_product` needs no such file.)
 */
const TemplateConsumedBlock: ComponentType = () => null;

/**
 * Discriminator-keyed dispatcher map: `block.blockType` → React component.
 *
 * Scope mirrors the Figma frame — the block types the current PDP design
 * supports. The type-level `ContentBlocksMap` constraint enforces an entry
 * for every member of the `CatalogContentBlockType` union, so adding a new
 * block type in `domain/catalog/blocks.ts` surfaces a compiler error here
 * until the map is updated.
 *
 * Note on `use_and_care_block` and `about_product`: both are mapped for
 * type-completeness but filtered out of `remainingBlocks` by the template —
 * they're consumed by `<AboutTheProduct/>` (the Use & Care guide affordance
 * and the Dimensions / Features / Care & Safety accordions respectively),
 * not visibly rendered as blocks. Their mapped components return `null` so
 * any stray rendering is a no-op.
 *
 * See `docs/PDP_ARCHITECTURE.md` ("Content blocks") for
 * the per-block adapter pattern.
 */
export interface BlockComponentBaseProps {
	parentSku?: string;
}

type ContentBlocksMap = {
	[K in CatalogContentBlockType]: ComponentType<
		{ block: BlockOf<K> } & BlockComponentBaseProps
	>;
};

export const contentBlocksMap: ContentBlocksMap = {
	feature_highlight_block: FeatureHighlight,
	guarantees_block: TemplateConsumedBlock,
	upsell_set_block: UpsellSet,
	see_in_action_block: SeeInAction,
	recipe_pairings_block: RecipePairings,
	use_and_care_block: UseAndCare,
	about_product: TemplateConsumedBlock,
	consultant_view_content: TemplateConsumedBlock
};

/**
 * Convenience exported for the dispatcher: hand it a `CatalogContentBlock`
 * and get the right component back, type-narrowed by the discriminator.
 *
 * Returns a no-op component for unknown `blockType` values so legacy or
 * not-yet-implemented block types on a live Algolia document don't crash
 * the page — they simply render nothing.
 */
export function pickBlockComponent<B extends CatalogContentBlock>(
	block: B
): ComponentType<{ block: B } & BlockComponentBaseProps> {
	const component = (
		contentBlocksMap as Record<
			string,
			ComponentType<{ block: B } & BlockComponentBaseProps> | undefined
		>
	)[block.blockType];
	return (component ?? UnknownBlock) as ComponentType<
		{ block: B } & BlockComponentBaseProps
	>;
}

function UnknownBlock(): null {
	return null;
}
